import React from 'react';

import { PageTitleText } from '~/app/components/ItemPage/sections/PageTitleSection/components';
import { ItemBlockedOverlay } from '~/app/components/ItemPage/components/ItemBlockedOverlay';
import { LegalFootnote } from '~/app/components/ItemPage/components/LegalFootnote';
import TextInputWithSubmit from '~/app/components/TextInput/TextInputWithSubmit';
import { useServiceAuth } from '~/app/lib/services/hooks/useServiceAuth';
import getServiceDisplayData from '~/app/lib/getServiceDisplayData';
import { useAppAlert } from '~/app/components/NextApp/lib/CoreUi';
import { ItemContext } from '~/app/components/ItemPage/types';
import { AddonTypes } from '~/app/lib/songwhipApi/types';
import { AUTH_GATE_COOKIE_NAME } from '~/config';
import { Clickable } from '~/app/components/Clickable';
import LockIcon from '~/app/components/Icon/LockIcon';
import Text from '~/app/components/Text';
import { useI18n } from '~/app/lib/i18n';
import Box from '~/app/components/Box';
import { ServiceTypes } from '~/types';

import { UsePageGateResult } from './usePageGate';

interface AuthGateProps {
  itemContext: ItemContext;
  unlockPageGate: UsePageGateResult['unlock'];
}

const SERVICES = [
  ServiceTypes.SPOTIFY,
  ServiceTypes.ITUNES,
  ServiceTypes.AMAZON_MUSIC,
  ServiceTypes.DEEZER,
];

export const AuthGate = ({ itemContext, unlockPageGate }: AuthGateProps) => {
  const appAlert = useAppAlert();
  const { t } = useI18n('item');

  const {
    data: { item },
    addons,
  } = itemContext;

  const addonData = addons[AddonTypes.AUTH_GATE];

  const onUnlock = async () => {
    unlockPageGate(AUTH_GATE_COOKIE_NAME, item.pagePath, 7);
  };

  const startServiceAuth = useServiceAuth({
    albumId: item.id,

    onSuccess: onUnlock,
    onError: (message) => {
      appAlert({ title: message });
    },
  });

  return (
    <ItemBlockedOverlay
      testId="authGate"
      padding="1.75rem"
      width="auto"
      minWidth="30rem"
      maxWidth="45rem"
      gap="normal"
      withLogo={false}
    >
      <Box margin="0 0 4.4rem">
        <PageTitleText text={item.name} />
      </Box>
      <Box
        flexColumn
        justifyCenter
        gap="1.75rem"
        padding="3.9rem 2.9rem"
        style={{
          borderRadius: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <LockIcon size="3rem" />
        <Text size="1.7rem" centered>
          {t('authGate.hint')}
        </Text>
        <Box tag="ul" flexBox gap="1.75rem">
          {SERVICES.map((service) => {
            const data = getServiceDisplayData(service);

            if (!data) return null;

            return (
              <Box key={data.key} tag="li">
                <Clickable
                  onClick={() => {
                    startServiceAuth({
                      serviceType: data.key,
                      fallbackUrl: location.href,
                    });
                  }}
                >
                  <data.Icon size="3.5rem" />
                </Clickable>
              </Box>
            );
          })}
        </Box>
        {addonData?.withEmail && (
          <Box fullWidth>
            <TextInputWithSubmit
              testId="emailInput"
              type="email"
              initialValue=""
              withShadow={false}
              buttonText={t('authGate.unlock')}
              placeholder={t('authGate.emailPlaceholder')}
              onSubmit={onUnlock}
            />
          </Box>
        )}
        <LegalFootnote />
      </Box>
    </ItemBlockedOverlay>
  );
};
